/**
 * whatsapp-checkout.js
 * ---------------------------------------------------------------------------
 * Checkout alternativo (One-Page) -> WhatsApp para tiendas Shopify.
 *
 * Hace 2 cosas:
 *   1) GLOBAL (en todas las páginas): intercepta el botón nativo de
 *      "Proceder al pago" del carrito y del cart drawer, y redirige a la
 *      página personalizada de checkout (/pages/checkout-alternativo).
 *   2) EN LA PÁGINA DE CHECKOUT: valida el formulario, permite editar
 *      cantidades (Cart Ajax API), arma el mensaje y abre WhatsApp.
 *
 * Sin dependencias del tema (IIFE puro) -> funciona en Sense clásico y en
 * la arquitectura Horizon por igual.
 *
 * Configuración (opcional) vía window.WhatsAppCheckoutConfig, inyectada por
 * la sección checkout-alternativo.liquid:
 *   {
 *     number: "57XXXXXXXXXX",          // número de WhatsApp con indicativo, SIN '+'
 *     checkoutUrl: "/pages/checkout-alternativo",
 *     currency: "COP",
 *     locale: "es-CO",
 *     shippingLabel: "Gratis",
 *     interceptEnabled: true
 *   }
 * ---------------------------------------------------------------------------
 */
(function () {
  'use strict';

  /* ----------------------------- Config ----------------------------------- */
  var DEFAULTS = {
    number: '',
    checkoutUrl: '/pages/checkout-alternativo',
    currency: 'COP',
    locale: 'es-CO',
    shippingTitle: 'Transportadora',
    shippingLabel: 'A convenir',
    interceptEnabled: true,
  };

  function getConfig() {
    var cfg = window.WhatsAppCheckoutConfig || {};
    // Hereda locale/currency del sistema de descuentos si existe, para
    // mantener consistencia con el resto del tema.
    var tdc = window.TieredDiscountConfig || {};
    return {
      number: String(cfg.number || DEFAULTS.number).replace(/[^0-9]/g, ''),
      checkoutUrl: cfg.checkoutUrl || DEFAULTS.checkoutUrl,
      currency: cfg.currency || tdc.currency || DEFAULTS.currency,
      locale: cfg.locale || tdc.locale || DEFAULTS.locale,
      shippingTitle: cfg.shippingTitle || DEFAULTS.shippingTitle,
      shippingLabel: cfg.shippingLabel || DEFAULTS.shippingLabel,
      interceptEnabled:
        cfg.interceptEnabled !== undefined ? !!cfg.interceptEnabled : DEFAULTS.interceptEnabled,
    };
  }

  /* --------------------------- Utilidades --------------------------------- */

  // Selectores que cubren AMBAS arquitecturas (Sense clásico + Horizon).
  var CHECKOUT_BTN_SELECTOR =
    '#checkout, #CartDrawer-Checkout, button[name="checkout"], .cart__checkout-button';

  var moneyFormatter = null;
  function formatMoney(cents, cfg) {
    // Shopify entrega los precios en la subunidad (cents = pesos * 100 para COP).
    var amount = (Number(cents) || 0) / 100;
    try {
      if (!moneyFormatter) {
        moneyFormatter = new Intl.NumberFormat(cfg.locale, {
          style: 'currency',
          currency: cfg.currency,
          minimumFractionDigits: 0,
          maximumFractionDigits: 0,
        });
      }
      return moneyFormatter.format(amount);
    } catch (e) {
      return '$' + Math.round(amount).toLocaleString('es-CO');
    }
  }

  function fetchCartJson() {
    return fetch(window.Shopify && window.Shopify.routes ? window.Shopify.routes.root + 'cart.js' : '/cart.js', {
      headers: { Accept: 'application/json' },
    }).then(function (r) {
      return r.json();
    });
  }

  /* ===================================================================== *
   *  PARTE 1: Interceptación global del checkout nativo
   * ===================================================================== */

  function isCheckoutTrigger(el) {
    return el && typeof el.closest === 'function' && el.closest(CHECKOUT_BTN_SELECTOR);
  }

  function redirectToCheckout(cfg) {
    // Evita bucle si ya estamos en la página de checkout alternativo.
    if (window.location.pathname.indexOf(cfg.checkoutUrl) === 0) return;
    window.location.href = cfg.checkoutUrl;
  }

  function installInterception(cfg) {
    if (!cfg.interceptEnabled) return;

    // a) Click sobre el botón de checkout (captura, antes que el submit nativo).
    document.addEventListener(
      'click',
      function (e) {
        var btn = isCheckoutTrigger(e.target);
        if (!btn) return;
        e.preventDefault();
        e.stopPropagation();
        redirectToCheckout(cfg);
      },
      true
    );

    // b) Submit del formulario del carrito disparado por el botón "checkout"
    //    (cubre Enter / submits programáticos). El submitter nos dice qué botón fue.
    document.addEventListener(
      'submit',
      function (e) {
        var form = e.target;
        if (!form || form.method.toLowerCase() !== 'post') return;
        var action = (form.getAttribute('action') || '').split('?')[0];
        var isCartForm = /\/cart$/.test(action);
        var submitter = e.submitter || document.activeElement;
        var byCheckout = submitter && submitter.name === 'checkout';
        if (!isCartForm && !byCheckout) return;
        if (!byCheckout) return; // dentro de un cart form, solo interceptamos el botón "checkout"
        e.preventDefault();
        e.stopPropagation();
        redirectToCheckout(cfg);
      },
      true
    );
  }

  /* ===================================================================== *
   *  PARTE 2: Lógica de la página de checkout alternativo
   * ===================================================================== */

  function initCheckoutPage(cfg) {
    var root = document.getElementById('wa-checkout');
    var form = document.getElementById('wa-checkout-form');
    if (!root || !form) return; // No estamos en la página de checkout.

    var summary = document.getElementById('wa-checkout-summary');
    var submitBtn = document.getElementById('wa-checkout-submit');

    /* --------------- 2.0 Autocomplete de Ciudad --------------- */
    initCityAutocomplete();
    updateMedellinPayment();

    /* --- Guardar/restaurar el formulario al recargar por cambio de cantidad --- */
    var WA_FORM_KEY = 'waCheckoutForm';
    restoreFormState();
    function saveFormState() {
      try {
        var data = {};
        var fs = form.querySelectorAll('[name]');
        for (var i = 0; i < fs.length; i++) {
          var f = fs[i];
          if (f.type === 'radio') { if (f.checked) data[f.name] = f.value; }
          else data[f.name] = f.value;
        }
        sessionStorage.setItem(WA_FORM_KEY, JSON.stringify(data));
      } catch (e) {}
    }
    function restoreFormState() {
      try {
        var raw = sessionStorage.getItem(WA_FORM_KEY);
        if (!raw) return;
        sessionStorage.removeItem(WA_FORM_KEY);
        var data = JSON.parse(raw);
        Object.keys(data).forEach(function (name) {
          var els = form.querySelectorAll('[name="' + name + '"]');
          for (var i = 0; i < els.length; i++) {
            var el = els[i];
            if (el.type === 'radio') el.checked = el.value === data[name];
            else el.value = data[name];
          }
        });
        updateMedellinPayment();
      } catch (e) {}
    }

    // "Contra entrega" solo se muestra si la ciudad seleccionada es Medellín.
    function updateMedellinPayment() {
      var medOption = form.querySelector('[data-wa-pay-medellin]');
      if (!medOption) return;
      var cityField = getField('city');
      var v = (cityField ? cityField.value : '')
        .toLowerCase()
        .normalize('NFD')
        .replace(/[̀-ͯ]/g, '')
        .trim();
      var isMed = v === 'medellin';
      medOption.hidden = !isMed;
      if (!isMed) {
        var radio = medOption.querySelector('input');
        if (radio && radio.checked) radio.checked = false;
      }
    }

    function initCityAutocomplete() {
      var wrap = form.querySelector('[data-wa-autocomplete]');
      if (!wrap) return;
      var input = wrap.querySelector('input');
      var listEl = wrap.querySelector('.wa-autocomplete__list');
      if (!input || !listEl) return;
      var deptSelect = getField('department');
      var current = [];
      var activeIndex = -1;

      function norm(s) {
        return (s || '').toLowerCase().normalize('NFD').replace(/[̀-ͯ]/g, '');
      }
      function closeList() {
        listEl.hidden = true;
        listEl.innerHTML = '';
        input.setAttribute('aria-expanded', 'false');
        activeIndex = -1;
      }
      function openList(matches) {
        current = matches;
        if (!matches.length) { closeList(); return; }
        listEl.innerHTML = matches
          .map(function (m, i) {
            return (
              '<li class="wa-autocomplete__item" role="option" id="wa-city-opt-' + i + '"' +
              ' data-city="' + escapeHtml(m[0]) + '" data-dept="' + escapeHtml(m[1]) + '">' +
              escapeHtml(m[0]) + '<span class="wa-autocomplete__dept">' + escapeHtml(m[1]) + '</span></li>'
            );
          })
          .join('');
        listEl.hidden = false;
        input.setAttribute('aria-expanded', 'true');
        activeIndex = -1;
      }
      function choose(city, dept) {
        input.value = city;
        if (deptSelect && dept) { deptSelect.value = dept; clearError(deptSelect); }
        clearError(input);
        closeList();
        updateMedellinPayment();
      }
      input.addEventListener('input', function () {
        updateMedellinPayment();
        var q = norm(input.value.trim());
        if (q.length < 2) { closeList(); return; }
        var cities = window.CO_CITIES || [];
        var pre = [], sub = [];
        for (var i = 0; i < cities.length; i++) {
          var n = norm(cities[i][0]);
          if (n.indexOf(q) === 0) pre.push(cities[i]);
          else if (n.indexOf(q) > 0) sub.push(cities[i]);
        }
        openList(pre.concat(sub).slice(0, 10));
      });
      // mousedown (no click) para que el blur no cierre la lista antes de seleccionar
      listEl.addEventListener('mousedown', function (e) {
        var li = e.target.closest('[data-city]');
        if (!li) return;
        e.preventDefault();
        choose(li.getAttribute('data-city'), li.getAttribute('data-dept'));
      });
      input.addEventListener('keydown', function (e) {
        if (listEl.hidden) return;
        var items = listEl.querySelectorAll('[role="option"]');
        if (e.key === 'ArrowDown') { e.preventDefault(); activeIndex = Math.min(activeIndex + 1, items.length - 1); paint(items); }
        else if (e.key === 'ArrowUp') { e.preventDefault(); activeIndex = Math.max(activeIndex - 1, 0); paint(items); }
        else if (e.key === 'Enter') { if (activeIndex >= 0 && current[activeIndex]) { e.preventDefault(); choose(current[activeIndex][0], current[activeIndex][1]); } }
        else if (e.key === 'Escape') { closeList(); }
      });
      function paint(items) {
        for (var i = 0; i < items.length; i++) {
          var on = i === activeIndex;
          items[i].classList.toggle('is-active', on);
          if (on && items[i].scrollIntoView) items[i].scrollIntoView({ block: 'nearest' });
        }
      }
      input.addEventListener('blur', function () { setTimeout(closeList, 150); });
    }

    /* ----------------- 2.1 Editar cantidades (Cart Ajax) ----------------- */
    if (summary) {
      summary.addEventListener('click', function (e) {
        var btn = e.target.closest('[data-wa-qty]');
        if (!btn) return;
        e.preventDefault();
        var lineItem = btn.closest('[data-wa-line]');
        if (!lineItem) return;
        var line = parseInt(lineItem.getAttribute('data-wa-line'), 10);
        var current = parseInt(lineItem.getAttribute('data-wa-quantity'), 10) || 0;
        var dir = btn.getAttribute('data-wa-qty'); // "inc" | "dec" | "remove"
        var next = current;
        if (dir === 'inc') next = current + 1;
        else if (dir === 'dec') next = Math.max(0, current - 1);
        else if (dir === 'remove') next = 0;
        if (next === current) return;
        updateLineQuantity(line, next, cfg);
      });
    }

    function setBusy(busy) {
      if (summary) summary.setAttribute('aria-busy', busy ? 'true' : 'false');
    }

    function updateLineQuantity(line, quantity, cfg) {
      setBusy(true);
      saveFormState();
      var body = JSON.stringify({ line: line, quantity: quantity });
      var url =
        window.Shopify && window.Shopify.routes
          ? window.Shopify.routes.root + 'cart/change.js'
          : '/cart/change.js';
      // Recargamos para que el servidor recalcule el precio mayorista (tier-aware).
      // El formulario se restaura desde sessionStorage al cargar.
      fetch(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: body,
      })
        .then(function () { window.location.reload(); })
        .catch(function () { window.location.reload(); });
    }

    function renderSummary(cart, cfg) {
      if (!summary) return;
      var list = summary.querySelector('[data-wa-lines]');
      if (list) {
        if (!cart.items || cart.items.length === 0) {
          // Carrito vacío -> volver al inicio.
          window.location.href = '/';
          return;
        }
        list.innerHTML = cart.items.map(function (item, i) { return lineItemHTML(item, i + 1, cfg); }).join('');
      }
      setText(summary.querySelector('[data-wa-subtotal]'), formatMoney(cart.original_total_price, cfg));
      setText(summary.querySelector('[data-wa-total]'), formatMoney(cart.total_price, cfg));
      var savings = (cart.original_total_price || 0) - (cart.total_price || 0);
      var savingsRow = summary.querySelector('[data-wa-savings-row]');
      if (savingsRow) {
        if (savings > 0) {
          savingsRow.hidden = false;
          setText(savingsRow.querySelector('[data-wa-savings]'), '−' + formatMoney(savings, cfg));
        } else {
          savingsRow.hidden = true;
        }
      }
      var count = summary.querySelector('[data-wa-count]');
      if (count) setText(count, String(cart.item_count));
    }

    function lineItemHTML(item, line, cfg) {
      var img = item.image
        ? '<img class="wa-line__img" src="' +
          imgUrl(item.image, 160) +
          '" alt="" width="64" height="64" loading="lazy">'
        : '<div class="wa-line__img wa-line__img--placeholder"></div>';
      var opts = '';
      if (item.options_with_values && item.options_with_values.length) {
        opts = item.options_with_values
          .filter(function (o) { return o.value && o.value !== 'Default Title'; })
          .map(function (o) { return '<span class="wa-line__opt">' + escapeHtml(o.name + ': ' + o.value) + '</span>'; })
          .join('');
      }
      var priceHtml =
        item.original_line_price !== item.final_line_price
          ? '<s class="wa-line__old">' + formatMoney(item.original_line_price, cfg) + '</s> ' +
            '<span class="wa-line__price">' + formatMoney(item.final_line_price, cfg) + '</span>'
          : '<span class="wa-line__price">' + formatMoney(item.final_line_price, cfg) + '</span>';
      return (
        '<li class="wa-line" data-wa-line="' + line + '" data-wa-quantity="' + item.quantity + '">' +
          '<div class="wa-line__media">' + img + '<span class="wa-line__badge">' + item.quantity + '</span></div>' +
          '<div class="wa-line__info">' +
            '<p class="wa-line__title">' + escapeHtml(item.product_title || item.title) + '</p>' +
            (opts ? '<div class="wa-line__opts">' + opts + '</div>' : '') +
            '<div class="wa-line__qty">' +
              '<button type="button" class="wa-qty__btn" data-wa-qty="dec" aria-label="Disminuir">−</button>' +
              '<span class="wa-qty__val">' + item.quantity + '</span>' +
              '<button type="button" class="wa-qty__btn" data-wa-qty="inc" aria-label="Aumentar">+</button>' +
              '<button type="button" class="wa-qty__remove" data-wa-qty="remove" aria-label="Quitar">Quitar</button>' +
            '</div>' +
          '</div>' +
          '<div class="wa-line__amount">' + priceHtml + '</div>' +
        '</li>'
      );
    }

    /* ----------------------- 2.2 Validación ------------------------------ */
    var REQUIRED = ['email', 'phone', 'first_name', 'last_name', 'id_number', 'country', 'address', 'city', 'department'];

    function getField(name) {
      return form.querySelector('[name="' + name + '"]');
    }

    function showError(field, message) {
      if (!field) return;
      field.setAttribute('aria-invalid', 'true');
      var wrap = field.closest('.wa-field') || field.parentNode;
      var err = wrap.querySelector('.wa-field__error');
      if (!err) {
        err = document.createElement('p');
        err.className = 'wa-field__error';
        wrap.appendChild(err);
      }
      err.textContent = message;
    }

    function clearError(field) {
      if (!field) return;
      field.removeAttribute('aria-invalid');
      var wrap = field.closest('.wa-field') || field.parentNode;
      var err = wrap.querySelector('.wa-field__error');
      if (err) err.textContent = '';
    }

    function validate() {
      var ok = true;
      var firstInvalid = null;

      REQUIRED.forEach(function (name) {
        var f = getField(name);
        if (!f) return;
        var val = (f.value || '').trim();
        clearError(f);
        if (!val) {
          showError(f, 'Este campo es obligatorio.');
          ok = false;
          firstInvalid = firstInvalid || f;
        }
      });

      // Email
      var email = getField('email');
      if (email && email.value.trim() && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.value.trim())) {
        showError(email, 'Ingresa un correo válido.');
        ok = false;
        firstInvalid = firstInvalid || email;
      }

      // Teléfono (mínimo 7 dígitos)
      var phone = getField('phone');
      if (phone && phone.value.trim() && (phone.value.replace(/[^0-9]/g, '').length < 7)) {
        showError(phone, 'Ingresa un número de celular válido.');
        ok = false;
        firstInvalid = firstInvalid || phone;
      }

      // Método de pago (radio obligatorio)
      var pay = form.querySelector('[name="payment_method"]:checked');
      var payWrap = form.querySelector('[data-wa-payment]');
      if (payWrap) {
        var payErr = payWrap.querySelector('.wa-field__error');
        if (payErr) payErr.textContent = '';
        if (!pay) {
          if (!payErr) {
            payErr = document.createElement('p');
            payErr.className = 'wa-field__error';
            payWrap.appendChild(payErr);
          }
          payErr.textContent = 'Selecciona un método de pago.';
          ok = false;
          firstInvalid = firstInvalid || payWrap;
        }
      }

      if (!ok && firstInvalid && firstInvalid.scrollIntoView) {
        firstInvalid.scrollIntoView({ behavior: 'smooth', block: 'center' });
        if (firstInvalid.focus) firstInvalid.focus({ preventScroll: true });
      }
      return ok;
    }

    // Limpia el error de un campo cuando el usuario lo corrige.
    form.addEventListener('input', function (e) {
      if (e.target && e.target.getAttribute('aria-invalid') === 'true') clearError(e.target);
    });

    /* ------------------- 2.3 Construcción del mensaje -------------------- */
    function buildMessage(cfg) {
      var v = function (name) {
        var f = getField(name);
        return f ? (f.value || '').trim() : '';
      };
      var payEl = form.querySelector('[name="payment_method"]:checked');
      var payment = payEl ? payEl.value : '';

      var L = [];
      L.push('*¡Nuevo Pedido Confirmado!* 🎉');
      L.push('');
      L.push('*👤 Datos del Cliente*');
      L.push('*Nombre:* ' + v('first_name') + ' ' + v('last_name'));
      L.push('*Cédula:* ' + v('id_number'));
      L.push('*Celular:* ' + v('phone'));
      L.push('*Correo:* ' + v('email'));
      L.push('');
      L.push('*🚚 Datos de envío*');
      var addr = v('address');
      if (v('address2')) addr += ', ' + v('address2');
      L.push('*Dirección:* ' + addr);
      L.push('*Ciudad:* ' + v('city'));
      L.push('*Departamento:* ' + v('department'));
      L.push('*País:* ' + v('country'));
      L.push('');
      L.push('*🛒 Detalle del Pedido*');
      // Precios MAYORISTA (reales), renderizados por el servidor en data-* (el detal nunca se cobra).
      var lines = summary ? summary.querySelectorAll('[data-wa-line]') : [];
      for (var i = 0; i < lines.length; i++) {
        var li = lines[i];
        var title = li.getAttribute('data-wa-title') || '';
        var variant = li.getAttribute('data-wa-variant');
        var vtxt = variant && variant !== 'Default Title' ? ' (' + variant + ')' : '';
        var qty = li.getAttribute('data-wa-quantity') || '';
        var lineCents = parseInt(li.getAttribute('data-wa-mayorista-line'), 10) || 0;
        L.push('• ' + title + vtxt + ' x' + qty + ' — ' + formatMoney(lineCents, cfg));
      }
      L.push('');
      L.push('*💳 Método de Pago:* ' + payment);
      L.push('*🚚 ' + cfg.shippingTitle + ':* ' + cfg.shippingLabel);
      var savRow = summary ? summary.querySelector('[data-wa-savings-row]') : null;
      if (savRow && !savRow.hidden) {
        var sp = savRow.querySelectorAll('span');
        if (sp.length >= 2) L.push('*🏷️ ' + sp[0].textContent.trim() + ':* ' + sp[1].textContent.trim());
      }
      var totalEl = summary ? summary.querySelector('[data-wa-total]') : null;
      var totalCents = totalEl ? (parseInt(totalEl.getAttribute('data-wa-total-cents'), 10) || 0) : 0;
      L.push('');
      L.push('*💰 TOTAL A PAGAR: ' + formatMoney(totalCents, cfg) + '*');

      return L.join('\n');
    }

    /* ------------------------- 2.4 Submit -------------------------------- */
    form.addEventListener('submit', function (e) {
      e.preventDefault();
      if (!validate()) return;

      if (!cfg.number) {
        alert(
          'El número de WhatsApp no está configurado. Configúralo en el personalizador del tema (sección "Checkout alternativo") antes de continuar.'
        );
        return;
      }

      var lines = summary ? summary.querySelectorAll('[data-wa-line]') : [];
      if (!lines.length) {
        alert('Tu carrito está vacío.');
        window.location.href = '/';
        return;
      }

      if (submitBtn) {
        submitBtn.disabled = true;
        submitBtn.classList.add('is-loading');
      }

      var text = buildMessage(cfg);
      var url = 'https://wa.me/' + cfg.number + '?text=' + encodeURIComponent(text);
      window.location.href = url;
    });
  }

  /* --------------------------- Helpers DOM -------------------------------- */
  function setText(el, text) {
    if (el) el.textContent = text;
  }
  function escapeHtml(s) {
    return String(s == null ? '' : s)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;');
  }
  function imgUrl(src, width) {
    if (!src) return '';
    // src de cart.js ya es una URL de CDN; añadimos width si es posible.
    try {
      var u = new URL(src, window.location.origin);
      u.searchParams.set('width', width);
      return u.toString();
    } catch (e) {
      return src;
    }
  }

  /* ------------------------------ Init ------------------------------------ */
  function init() {
    // El script puede incluirse 2 veces (global en theme.liquid + en la
    // sección). Inicializamos una sola vez para no duplicar listeners.
    if (window.__waCheckoutInit) return;
    window.__waCheckoutInit = true;
    var cfg = getConfig();
    installInterception(cfg);
    initCheckoutPage(cfg);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
